import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import Lightbox from "@/components/Lightbox";

const AVAILABILITY_LABEL = {
  available: "Available",
  sold: "Sold",
  reserved: "Reserved",
  nfs: "Not for sale",
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "available", label: "Available" },
  { key: "sold", label: "Sold" },
];

export default function Overview() {
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api
      .get("/artworks")
      .then((r) => setArtworks(r.data))
      .finally(() => setLoading(false));
  }, []);

  const visible = useMemo(() => {
    if (filter === "all") return artworks;
    return artworks.filter((a) => a.availability === filter);
  }, [artworks, filter]);

  return (
    <div className="max-w-[1500px] mx-auto px-6 sm:px-10 py-16 fade-up" data-testid="overview-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
        <div>
          <p className="plaque-label">Overview</p>
          <h1 className="font-serif-display font-light text-5xl sm:text-7xl leading-[0.95] mt-3">
            Every piece, at a glance.
          </h1>
          <p className="text-zinc-400 mt-4 max-w-xl">
            The whole collection on one wall. Click any work to enlarge.
          </p>
        </div>
        <div className="flex items-center gap-1 border border-white/10 rounded-sm p-1 self-start md:self-end" data-testid="overview-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 text-xs tracking-[0.2em] uppercase transition-colors ${
                filter === f.key
                  ? "bg-[#D4AF37] text-black"
                  : "text-zinc-400 hover:text-white"
              }`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-zinc-500" data-testid="overview-loading">Loading the collection…</p>
      ) : visible.length === 0 ? (
        <div className="border border-dashed border-white/10 p-16 text-center text-zinc-500" data-testid="overview-empty">
          No works {filter !== "all" ? `marked "${filter}"` : "to show yet"}.
        </div>
      ) : (
        <div
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6"
          data-testid="overview-grid"
        >
          {visible.map((a, i) => (
            <motion.button
              key={a.id}
              type="button"
              onClick={() => setActive(a)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: Math.min(i * 0.03, 0.4) }}
              className="group text-left"
              data-testid={`overview-thumb-${a.id}`}
            >
              <div className="relative aspect-square bg-[#0F0F0F] border border-white/5 overflow-hidden">
                <img
                  src={a.thumb_data || a.image_data}
                  alt={a.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-contain transition-transform duration-700 group-hover:scale-[1.03]"
                />
                {a.availability && a.availability !== "available" && (
                  <span className="absolute top-2 right-2 text-[9px] tracking-[0.25em] uppercase text-[#D4AF37] bg-black/70 border border-[#D4AF37]/40 px-2 py-0.5">
                    {AVAILABILITY_LABEL[a.availability] || a.availability}
                  </span>
                )}
              </div>
              <div className="mt-3 flex items-baseline justify-between gap-2">
                <p className="font-serif-display text-base sm:text-lg italic text-zinc-200 truncate">
                  {a.title}
                </p>
                {a.year && (
                  <span className="plaque-label whitespace-nowrap">{a.year}</span>
                )}
              </div>
            </motion.button>
          ))}
        </div>
      )}

      {!loading && (
        <p className="plaque-label mt-12 text-center" data-testid="overview-count">
          {visible.length} of {artworks.length} {artworks.length === 1 ? "work" : "works"}
        </p>
      )}

      <Lightbox artwork={active} onClose={() => setActive(null)} />
    </div>
  );
}
